---
title: "Connect Notion"
description: "The Notion integration keeps a candidate and job database synced both directions, so the people who live in Notion never have to learn a new tool."
category: "getting-started"
keywords:
  - "Notion ATS integration"
  - "sync candidates to Notion"
  - "Notion recruiting database"
  - "two-way Notion sync"
schema: "HowTo"
nav:
  section: "getting-started"
  order: 40
  parent: "/getting-started"
---

The Notion integration mirrors your candidates and jobs into a Notion database and keeps both sides in sync. It's optional — connect it only if part of your team already runs hiring out of Notion.

## What it does

Chosen syncs candidates and jobs to a Notion database you choose, and changes flow **both directions**: edit a candidate's note in Notion and it shows up in Chosen, and the other way around. The point is that a hiring manager who lives in Notion can keep living there.

## How to connect

<Steps>
  <li>Go to **Settings → Integrations** and click **Connect** next to Notion.</li>
  <li>Authorize Chosen in the Notion window and choose which workspace to grant.</li>
  <li>Back in Chosen, **pick the database** to sync into. Nothing syncs until you've selected one — this step is easy to miss.</li>
  <li>Review the field mapping — which Chosen fields map to which Notion properties — and adjust it if you need to.</li>
</Steps>

## What syncs, and when

- New candidates and jobs appear in Notion shortly after they're created in Chosen.
- Edits sync both ways.
- When you close a job in Chosen, its Notion page is archived automatically — your Notion database doesn't fill up with dead roles.

<Callout type="warning" title="Reconnecting clears your database selection">
If you disconnect and reconnect Notion — or connect a different workspace — Chosen forgets which database you were syncing into. You'll need to pick the database again. Until you do, nothing syncs, and it fails quietly. After any reconnect, go straight back to **Settings → Integrations** and re-select the database.
</Callout>

## When to skip it

Notion is one workspace per organization, and it's genuinely optional. If your team doesn't already run hiring in Notion, connecting it just adds a second place for data to drift. Skip it.
